// src/utils/validation.js

/**
 * Normalize phone number to digits only (removes dashes, spaces, and other non-digit characters)
 * @param {string} phone - Phone number to normalize
 * @returns {string} - Phone number with only digits
 */
export const normalizePhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

/**
 * Validate Israeli phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid
 */
export const isValidIsraeliPhone = (phone) => {
  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Israeli phone patterns:
  // Mobile: 05X-XXX-XXXX (10 digits starting with 05)
  // Landline: 0X-XXX-XXXX (9 digits starting with 02, 03, 04, 08, 09)
  
  if (cleanPhone.length === 10 && cleanPhone.startsWith('05')) {
    return true; // Mobile
  }
  
  if (cleanPhone.length === 9 && /^0[2-4,8-9]/.test(cleanPhone)) {
    return true; // Landline
  }
  
  return false;
};

/**
 * Format Israeli phone number
 * @param {string} phone - Phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatIsraeliPhone = (phone) => {
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.length === 10 && cleanPhone.startsWith('05')) {
    // Mobile: 050-123-4567
    return `${cleanPhone.slice(0, 3)}-${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
  }
  
  if (cleanPhone.length === 9 && /^0[2-4,8-9]/.test(cleanPhone)) {
    // Landline: 02-123-4567
    return `${cleanPhone.slice(0, 2)}-${cleanPhone.slice(2, 5)}-${cleanPhone.slice(5)}`;
  }
  
  return phone; // Return as-is if doesn't match patterns
};

/**
 * Validate business name
 * @param {string} name - Business name to validate
 * @returns {object} - Validation result
 */
export const validateBusinessName = (name) => {
  if (!name || !name.trim()) {
    return { isValid: false, error: 'שם העסק הוא שדה חובה' };
  }
  
  if (name.trim().length < 2) {
    return { isValid: false, error: 'שם העסק חייב להכיל לפחות 2 תווים' };
  }
  
  if (name.trim().length > 100) {
    return { isValid: false, error: 'שם העסק לא יכול להכיל יותר מ-100 תווים' };
  }
  
  return { isValid: true };
};

/**
 * Validate business type
 * @param {string} type - Business type to validate
 * @returns {object} - Validation result
 */
export const validateBusinessType = (type) => {
  if (!type || !type.trim()) {
    return { isValid: false, error: 'סוג העסק הוא שדה חובה' };
  }
  
  if (type.trim().length < 2) {
    return { isValid: false, error: 'סוג העסק חייב להכיל לפחות 2 תווים' };
  }
  
  if (type.trim().length > 50) {
    return { isValid: false, error: 'סוג העסק לא יכול להכיל יותר מ-50 תווים' };
  }
  
  return { isValid: true };
};

/**
 * Validate phone number
 * @param {string} phone - Phone number to validate
 * @returns {object} - Validation result
 */
export const validatePhone = (phone) => {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: 'מספר טלפון הוא שדה חובה' };
  }
  
  if (!isValidIsraeliPhone(phone)) {
    return { 
      isValid: false, 
      error: 'מספר טלפון לא תקין. הזן מספר טלפון ישראלי תקין (למשל: 050-123-4567)' 
    };
  }
  
  return { isValid: true };
};

/**
 * Validate business address
 * @param {string} address - Address to validate
 * @returns {object} - Validation result
 */
export const validateAddress = (address) => {
  if (!address || !address.trim()) {
    return { isValid: false, error: 'כתובת העסק היא שדה חובה' };
  }
  
  if (address.trim().length < 5) {
    return { isValid: false, error: 'כתובת העסק חייבת להכיל לפחות 5 תווים' };
  }
  
  if (address.trim().length > 200) {
    return { isValid: false, error: 'כתובת העסק לא יכולה להכיל יותר מ-200 תווים' };
  }
  
  return { isValid: true };
};

/**
 * Validate complete business info object
 * @param {object} businessInfo - Business info to validate
 * @returns {object} - Validation result with errors object
 */
export const validateBusinessInfo = (businessInfo) => {
  const errors = {};
  let isValid = true;
  
  // Validate business name
  const nameValidation = validateBusinessName(businessInfo.businessName);
  if (!nameValidation.isValid) {
    errors.businessName = nameValidation.error;
    isValid = false;
  }
  
  // Validate business type
  const typeValidation = validateBusinessType(businessInfo.businessType);
  if (!typeValidation.isValid) {
    errors.businessType = typeValidation.error;
    isValid = false;
  }
  
  // Validate phone
  const phoneValidation = validatePhone(businessInfo.phone);
  if (!phoneValidation.isValid) {
    errors.phone = phoneValidation.error;
    isValid = false;
  }
  
  // Validate address
  const addressValidation = validateAddress(businessInfo.address);
  if (!addressValidation.isValid) {
    errors.address = addressValidation.error;
    isValid = false;
  }
  
  return {
    isValid,
    errors
  };
};

export const sanitizeBusinessInfo = (businessInfo) => {
  return {
    businessName: businessInfo.businessName?.trim() || '',
    businessType: businessInfo.businessType?.trim() || '',
    phone: formatIsraeliPhone(businessInfo.phone || ''),
    address: businessInfo.address?.trim() || '',
    business_number: businessInfo.business_number?.trim() || '',
    website: businessInfo.website?.trim() || '',
    email: businessInfo.email?.trim().toLowerCase() || '',
    displayName: businessInfo.displayName?.trim() || '',
    services: businessInfo.services || [] // For when we add services
  };
};